import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";
import { Plus, Minus, ShoppingCart, Sparkles, X, ZoomIn } from "lucide-react";

// SECTIONS and GRADES are now loaded dynamically from the /catalog API

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [catalog, setCatalog] = useState({ sections: {}, grades: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useSearchParams();
  const { addItem } = useCart();
  const { format } = useCurrency();
  const [qtys, setQtys] = useState({});
  const [lightbox, setLightbox] = useState(null); // { src, alt }
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const section = search.get("section") || "All";
  const category = search.get("category") || "";
  const grade = search.get("grade") || "All";

  useEffect(() => {
    document.title = "Shop – Cheap4Teach";
    api.get("/catalog").then(r => setCatalog(r.data));
  }, []);

  const PER = 12;
  const buildParams = (pg) => {
    const params = { page: pg, limit: PER };
    if (section && section !== "All") params.section = section;
    if (category) params.category = category;
    if (grade && grade !== "All") params.grade = grade;
    return params;
  };
  useEffect(() => {
    let active = true;
    const ctrl = new AbortController();
    setLoading(true); setPage(1); setProducts([]);
    api.get("/products", { params: buildParams(1), signal: ctrl.signal }).then(r => {
      if (!active) return;
      const d = r.data;
      const items = Array.isArray(d) ? d : (d.items || []);
      setProducts(items);
      setHasMore(Array.isArray(d) ? false : (d.page < d.pages));
    }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; ctrl.abort(); };
  }, [section, category, grade]);
  const loadMore = () => {
    const next = page + 1;
    setLoadingMore(true);
    api.get("/products", { params: buildParams(next) }).then(r => {
      const d = r.data;
      const items = Array.isArray(d) ? [] : (d.items || []);
      setProducts(prev => [...prev, ...items]);
      setPage(next);
      setHasMore(!Array.isArray(d) && d.page < d.pages);
    }).finally(() => setLoadingMore(false));
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(search);
    if (!value || value === "All") next.delete(key);
    else next.set(key, value);
    if (key === "section") next.delete("category"); // reset category when section changes
    setSearch(next);
  };

  const clearAll = () => setSearch({});

  const qtyOf = (id) => qtys[id] || 1;
  const setQty = (id, v) => setQtys({ ...qtys, [id]: Math.max(1, v) });

  const handleAdd = (p) => {
    addItem(p, qtyOf(p.id));
    toast.success(`Added ${qtyOf(p.id)} × ${p.name} to cart!`);
  };

  const subCategories = section !== "All" ? (catalog.sections[section] || []) : [];
  const hasFilters = section !== "All" || category || grade !== "All";

  return (
    <>
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="shop-page">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="w-7 h-7 text-[#FFD166]" />
        <h1 className="text-4xl sm:text-5xl font-['Fredoka'] font-bold">Shop</h1>
      </div>
      <p className="c4t-desc text-base sm:text-lg mb-6">Filter by section, subject, or grade to find what fits your class.</p>

      {/* ── Filters: dropdowns on mobile, pills on md+ ── */}

      {/* MOBILE: compact dropdowns (hidden on md+) */}
      <div className="flex flex-col gap-3 mb-5 md:hidden">
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs font-bold uppercase text-[#455A64] mb-1 block">Section</label>
            <select
              value={section}
              onChange={e => setFilter("section", e.target.value)}
              className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm font-semibold bg-white shadow-[2px_2px_0_#000] focus:outline-none focus:ring-2 focus:ring-[#FF1F87]"
              data-testid="mobile-section-select"
            >
              {["All", ...Object.keys(catalog.sections)].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="text-xs font-bold uppercase text-[#455A64] mb-1 block">Grade</label>
            <select
              value={grade}
              onChange={e => setFilter("grade", e.target.value)}
              className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm font-semibold bg-white shadow-[2px_2px_0_#000] focus:outline-none focus:ring-2 focus:ring-[#FF1F87]"
              data-testid="mobile-grade-select"
            >
              {["All", ...catalog.grades].map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
        </div>
        {subCategories.length > 0 && (
          <div>
            <label className="text-xs font-bold uppercase text-[#455A64] mb-1 block">
              {section === "Charts by Subject" ? "Subject" : "Category"}
            </label>
            <select
              value={category}
              onChange={e => setFilter("category", e.target.value)}
              className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm font-semibold bg-white shadow-[2px_2px_0_#000] focus:outline-none focus:ring-2 focus:ring-[#FF1F87]"
              data-testid="mobile-category-select"
            >
              <option value="">All</option>
              {subCategories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* DESKTOP: pills (hidden on mobile) */}
      <div className="hidden md:block">
        <div className="mb-3" data-testid="section-filters">
          <p className="text-xs font-bold uppercase text-[#455A64] mb-2">Section</p>
          <div className="flex flex-wrap gap-2">
            {["All", ...Object.keys(catalog.sections)].map(s => (
              <button key={s} onClick={() => setFilter("section", s)}
                data-testid={`filter-section-${s.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                className={`c4t-pill ${section === s ? "active" : ""}`}>{s}</button>
            ))}
          </div>
        </div>
        {subCategories.length > 0 && (
          <div className="mb-3" data-testid="category-filters">
            <p className="text-xs font-bold uppercase text-[#455A64] mb-2">{section === "Charts by Subject" ? "Subject" : "Category"}</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilter("category", "")}
                className={`c4t-pill ${!category ? "active" : ""}`} data-testid="filter-category-all">All</button>
              {subCategories.map(c => (
                <button key={c} onClick={() => setFilter("category", c)}
                  data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                  className={`c4t-pill ${category === c ? "active" : ""}`}>{c}</button>
              ))}
            </div>
          </div>
        )}
        <div className="mb-6" data-testid="grade-filters">
          <p className="text-xs font-bold uppercase text-[#455A64] mb-2">Grade</p>
          <div className="flex flex-wrap gap-2">
            {["All", ...catalog.grades].map(g => (
              <button key={g} onClick={() => setFilter("grade", g)}
                data-testid={`filter-grade-${g.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                className={`c4t-pill ${grade === g ? "active" : ""}`}>{g}</button>
            ))}
          </div>
        </div>
      </div>

      {hasFilters && (
        <button onClick={clearAll} className="c4t-pill mb-6 !bg-[#FFF9F2]" data-testid="clear-filters-btn">
          <X className="w-3 h-3" /> Clear all filters
        </button>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="c4t-card !p-3 animate-pulse">
              <div className="aspect-square bg-[#FFF9F2] rounded-2xl" />
              <div className="h-4 bg-[#FFF9F2] rounded mt-3" />
              <div className="h-4 bg-[#FFF9F2] rounded mt-2 w-1/2" />
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="c4t-card text-center py-16" data-testid="no-products">
          <h3 className="text-2xl font-bold">No products here yet</h3>
          <p className="text-[#455A64] mt-2">Try a different filter or check back soon. Fresh items coming!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {products.map(p => {
            const soldOut = (p.stock ?? 0) <= 0;
            const lowStock = !soldOut && (p.stock ?? 0) <= 3;
            return (
            <div key={p.id} className="c4t-card !p-3 !pb-5 flex flex-col" data-testid={`product-card-${p.id}`}>
              <Link to={`/product/${p.id}`} className="block">
                <div className="relative aspect-square overflow-hidden rounded-2xl bg-[#FFF9F2] border-2 border-[#073B4C]/20 group">
                  {p.image ? (
                    <>
                      <img src={p.image} alt={p.name}
                        className={`w-full h-full object-cover hover:scale-105 transition-transform duration-500 ${soldOut ? "opacity-60" : ""}`} />
                      <button
                        onClick={e => { e.preventDefault(); setLightbox({ src: p.image, alt: p.name }); }}
                        className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all duration-200"
                        aria-label="View full image"
                      >
                        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 drop-shadow-lg transition-opacity duration-200" />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#073B4C]/30 text-xs">No image</div>
                  )}
                  {soldOut && (
                    <span className="absolute top-2 left-2 px-3 py-1 bg-black text-white text-xs font-['Fredoka'] font-bold rounded-full border-2 border-white shadow-[2px_2px_0_#000]"
                      data-testid={`sold-out-badge-${p.id}`}>SOLD OUT</span>
                  )}
                  {!soldOut && p.sale_price > 0 && (
                    <span className="absolute top-2 left-2 px-3 py-1 bg-[#FF1F87] text-white text-xs font-['Fredoka'] font-bold rounded-full border-2 border-white shadow-[2px_2px_0_#000]"
                      data-testid={`sale-badge-${p.id}`}>SALE</span>
                  )}
                </div>
              </Link>
              <div className="mt-3 flex-1">
                <span className="text-xs font-semibold text-[#118AB2] uppercase">{p.category || p.section}</span>
                <Link to={`/product/${p.id}`}>
                  <h3 className="font-['Fredoka'] font-bold text-lg leading-tight mt-1 hover:text-[#FF477E]">{p.name}</h3>
                </Link>
                <p className="c4t-desc text-sm mt-1 line-clamp-2">{p.description}</p>
                {p.sale_price > 0 ? (
                  <div className="flex items-baseline gap-2 mt-2">
                    <p className="text-[#FF1F87] font-bold text-2xl">{format(p.sale_price, p.currency)}</p>
                    <p className="text-gray-400 line-through text-sm">{format(p.price, p.currency)}</p>
                  </div>
                ) : (
                  <p className="text-[#FF1F87] font-bold text-2xl mt-2">{format(p.price, p.currency)}</p>
                )}
                {lowStock && (
                  <p className="text-xs font-bold text-[#FF6B1A] mt-1 animate-pulse" data-testid={`low-stock-${p.id}`}>
                    Only {p.stock} left!
                  </p>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 pb-1">
                <div className="flex items-center border-2 border-[#073B4C] rounded-full shrink-0">
                  <button onClick={() => setQty(p.id, qtyOf(p.id) - 1)} disabled={soldOut} className="px-2 py-1.5 disabled:opacity-40" data-testid={`qty-dec-${p.id}`}><Minus className="w-3 h-3" /></button>
                  <span className="w-7 text-center font-bold text-sm" data-testid={`qty-val-${p.id}`}>{qtyOf(p.id)}</span>
                  <button onClick={() => setQty(p.id, Math.min(p.stock || 1, qtyOf(p.id) + 1))} disabled={soldOut} className="px-2 py-1.5 disabled:opacity-40" data-testid={`qty-inc-${p.id}`}><Plus className="w-3 h-3" /></button>
                </div>
                <button onClick={() => handleAdd(p)} disabled={soldOut} title={soldOut ? "Sold Out" : "Add to Cart"}
                  className="flex sm:!hidden items-center justify-center p-2 shrink-0 disabled:cursor-not-allowed"
                  data-testid={`add-to-cart-${p.id}`}>
                  <ShoppingCart className={`w-6 h-6 ${soldOut ? "text-gray-400" : "text-[#FF1F87]"}`} />
                </button>
                <button onClick={() => handleAdd(p)} disabled={soldOut}
                  className={`!hidden sm:!flex items-center gap-1.5 c4t-btn !text-sm !py-2 !px-3 !normal-case !tracking-normal shrink-0 ${soldOut ? "c4t-btn-white !opacity-60 cursor-not-allowed" : "c4t-btn-pink"}`}
                  data-testid={`add-to-cart-d-${p.id}`}>
                  <ShoppingCart className="w-4 h-4" /> {soldOut ? "Sold Out" : "Add to Cart"}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
      {!loading && hasMore && (
        <div className="text-center mt-10">
          <button onClick={loadMore} disabled={loadingMore} className="c4t-btn c4t-btn-pink !px-10" data-testid="shop-load-more">{loadingMore ? "Loading..." : "Load more"}</button>
        </div>
      )}
    </div>

      {/* ── Image Lightbox ── */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#FF1F87] transition-colors"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-full max-h-[90vh] rounded-2xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-0 right-0 text-center text-white/70 text-sm font-['Fredoka']">{lightbox.alt}</p>
        </div>
      )}
    </>
  );
}
